import React, { useEffect, useMemo, useState } from 'react'
import { Box, FormControl, Grid, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material'
import dayjs from 'dayjs'

/**
 * DOBcalc
 * Material-UI DOB selector capturing Day / Month / Year
 * Props:
 *  - value: optional initial value as string 'YYYY-MM-DD' or object { year, month, day }
 *  - onChange: callback called with { year, month, day, iso } when selection changes
 *  - supply_DOB: callback called with iso string 'YYYY-MM-DD' when valid
 *  - pax_type: number (1=adult 12+, 2=child 2-12, 3=infant <2) used to validate DOB
 *  - minYear / maxYear: year range for the Year select
 *  - required: whether fields are required (adds simple validation)
 */
export default function DOBcalc({ value: initialValue, onChange, supply_DOB, pax_type = null, minYear = 1900, maxYear = new Date().getFullYear(), required = false, minDate = null, maxDate = null }) {
    const parseInitial = (v) => {
        if (!v) return { day: '', month: '', year: '' }
        if (typeof v === 'string') {
            const parts = v.split('-')
            if (parts.length === 3) return { year: parts[0], month: String(Number(parts[1])), day: String(Number(parts[2])) }
        }
        if (typeof v === 'object') {
            return { day: v.day || '', month: String(v.month || ''), year: String(v.year || '') }
        }
        return { day: '', month: '', year: '' }
    }

    const [day, setDay] = useState(parseInitial(initialValue).day)
    const [month, setMonth] = useState(parseInitial(initialValue).month)
    const [year, setYear] = useState(parseInitial(initialValue).year)
    const [touched, setTouched] = useState(false)

    useEffect(() => {
        // If the parent changes the initial value, reflect it
        const parsed = parseInitial(initialValue)
        setDay(parsed.day)
        setMonth(parsed.month)
        setYear(parsed.year)
    }, [initialValue])

    const months = useMemo(() => [
        { label: 'Jan', value: '1' }, { label: 'Feb', value: '2' }, { label: 'Mar', value: '3' }, { label: 'Apr', value: '4' }, { label: 'May', value: '5' }, { label: 'Jun', value: '6' }, { label: 'Jul', value: '7' }, { label: 'Aug', value: '8' }, { label: 'Sep', value: '9' }, { label: 'Oct', value: '10' }, { label: 'Nov', value: '11' }, { label: 'Dec', value: '12' }
    ], [])

    const years = useMemo(() => {
        // respect minDate/maxDate if provided
        const minY = minDate ? dayjs(minDate, 'YYYY-MM-DD', true).year() : minYear
        const maxY = maxDate ? dayjs(maxDate, 'YYYY-MM-DD', true).year() : maxYear
        const ys = []
        for (let y = maxY; y >= minY; y--) ys.push(String(y))
        return ys
    }, [minYear, maxYear, minDate, maxDate])

    const daysInMonth = useMemo(() => {
        if (!month || !year) return Array.from({ length: 31 }, (_, i) => String(i + 1))
        const count = dayjs(`${year}-${month}-01`).daysInMonth()
        return Array.from({ length: count }, (_, i) => String(i + 1))
    }, [month, year])

    const isComplete = day && month && year
    const isValid = isComplete && dayjs(`${year}-${month}-${day}`, 'YYYY-M-D', true).isValid()

    // Age-based validation per pax_type
    const isoDate = isValid ? dayjs(`${year}-${month}-${day}`, 'YYYY-M-D').format('YYYY-MM-DD') : null
    const ageYears = isoDate ? dayjs().diff(dayjs(isoDate), 'year') : null // integer years
    let ageValid = true
    if (pax_type != null && isoDate) {
        const pt = Number(pax_type)
        if (pt === 1) {
            // adult: 12 years or older
            ageValid = ageYears >= 12
        } else if (pt === 2) {
            // child: between 2 (inclusive) and 12 (exclusive)
            ageValid = ageYears >= 2 && ageYears < 12
        } else if (pt === 3) {
            // infant: below 2 years
            ageValid = ageYears < 2
        }
    }

    // check min/max date range if provided
    const minDateObj = minDate ? dayjs(minDate, 'YYYY-MM-DD', true) : null
    const maxDateObj = maxDate ? dayjs(maxDate, 'YYYY-MM-DD', true) : null
    const inRange = isoDate ? (!minDateObj || !dayjs(isoDate).isBefore(minDateObj, 'day')) && (!maxDateObj || !dayjs(isoDate).isAfter(maxDateObj, 'day')) : true

    const overallValid = isValid && (pax_type == null ? true : !!ageValid) && inRange

    useEffect(() => {
        const iso = isoDate
        console.log('DOB changed:', { day, month, year, iso, overallValid, ageYears });
        if (onChange) {
            onChange({ day: day || null, month: month || null, year: year || null, iso, valid: !!overallValid, ageYears })
        }
        if (typeof supply_DOB === 'function') {
                // only call supply_DOB when overall validation (including pax_type) passes
                if (overallValid && iso) supply_DOB(iso);
                if (overallValid && iso) console.log('DOB valid:', iso)
                if (overallValid && iso) console.log('supplying DOB:', iso);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [day, month, year, overallValid])

    const showError = required && touched && !overallValid
    const errorMessage = showError ? (
        !isValid ? 'Enter a valid date' :
            (isValid && pax_type === 1 && !ageValid) ? 'DOB must be 12 years or older' :
                (isValid && pax_type === 2 && !ageValid) ? 'DOB must be between 2 and 12 years old' :
                    (isValid && pax_type === 3 && !ageValid) ? 'DOB must be below 2 years old' :
                        (isValid && !inRange && minDateObj && !maxDateObj) ? `Date must be on or after ${minDateObj.format('DD-MM-YYYY')}` :
                            (isValid && !inRange && maxDateObj && !minDateObj) ? `Date must be on or before ${maxDateObj.format('DD-MM-YYYY')}` :
                                (isValid && !inRange && minDateObj && maxDateObj) ? `Date must be between ${minDateObj.format('DD-MM-YYYY')} and ${maxDateObj.format('DD-MM-YYYY')}` : ''
    ) : ''

    return (
        <Box sx={{ width: '100%' }} >
            <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={4}>
                    <FormControl fullWidth error={showError}>
                        <Select
                            value={day}
                            onChange={(e) => { setDay(e.target.value); setTouched(true) }}
                            displayEmpty
                            style={{ height: '3rem' }}
                        >
                            <MenuItem value="">DD</MenuItem>
                            {daysInMonth.map((d) => (
                                <MenuItem key={d} value={d}>{d.padStart(2, '0')}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={4}>
                    <FormControl fullWidth error={showError}>
                        <Select
                            value={month}
                            onChange={(e) => { setMonth(e.target.value); setTouched(true) }}
                            displayEmpty
                            style={{ height: '3rem' }}
                        >
                            <MenuItem value="">MM</MenuItem>
                            {months.map((m, i) => {
                                const num = String(i + 1).padStart(2, '0')
                                return (
                                    <MenuItem key={m.value} value={m.value}>{m.label} ({num})</MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={4}>
                    <FormControl fullWidth error={showError}>
                        <Select
                            value={year}
                            onChange={(e) => { setYear(e.target.value); setTouched(true) }}
                            displayEmpty
                            style={{ height: '3rem' }}
                        >
                            <MenuItem value="">YYYY</MenuItem>
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                        {/* Error helper removed from FormHelperText and moved to the dedicated paragraph below */}
                    </FormControl>
                </Grid>
            </Grid>
            {errorMessage ? (
                <p className='px-3 rounded-1 mt-3  py-2 text-bg-danger' role="alert" aria-live="polite">{errorMessage}</p>
            ) : null}
        </Box>
    )
}
